/** Exactly rate/scale helpers. All on-chain rates are WAD (1e18) fractions. */

const WAD = 1e18
const YEAR_SECONDS = 31_536_000

/** WAD annualized rate → percent (0.0547e18 → 5.47). */
export function exactlyWadRateToPercent(rate: bigint | undefined): number {
  if (!rate) return 0
  return (Number(rate) / WAD) * 100
}

/** WAD 1e18-scaled USD price → USD number. */
export function exactlyUsdPrice(usdPrice: bigint | undefined): number {
  if (!usdPrice) return 0
  return Number(usdPrice) / WAD
}

/**
 * WAD per-second late-repay `penaltyRate` → annualized percent. The penalty
 * accrues linearly per second on OVERDUE fixed debt (principal + fee) from
 * maturity until repaid, ON TOP of the owed amount. E.g. the live value
 * 52083333333/s ≈ 164%/yr ≈ 0.45%/day.
 */
export function exactlyPenaltyRateToAprPercent(
  penaltyRatePerSecond: bigint | undefined,
): number {
  if (!penaltyRatePerSecond) return 0
  return (Number(penaltyRatePerSecond) / WAD) * YEAR_SECONDS * 100
}

/**
 * Approximate floating DEPOSIT APR: borrow-rate income spread over deposits,
 * with utilization measured over BOTH floating debt and the backup lent into
 * fixed pools (fixed borrowers draw on floating liquidity and pay at least
 * comparable rates, so backup principal earns too — a market whose borrowing
 * is all fixed would otherwise display 0%). Still a lower-bound approximation
 * (ignores the earnings-accumulator drip and backup-fee premium); exact yield
 * needs the accumulator model.
 */
export function exactlyFloatingDepositAprPercent(
  floatingBorrowRate: bigint | undefined,
  floatingDebt: bigint | undefined,
  floatingBackupBorrowed: bigint | undefined,
  floatingAssets: bigint | undefined,
): number {
  if (!floatingBorrowRate || !floatingAssets) return 0
  const lent = Number((floatingDebt ?? 0n) + (floatingBackupBorrowed ?? 0n))
  const assets = Number(floatingAssets)
  if (assets <= 0 || lent <= 0) return 0
  return (Number(floatingBorrowRate) / WAD) * (lent / assets) * 100
}

/**
 * Effective pairwise LTV between two Exactly markets. Exactly health is
 * Σ(collateral × adjustFactor_c) ≥ Σ(debt / adjustFactor_b), so the max
 * borrow of market B against collateral in market A is adjF_A × adjF_B —
 * MULTIPLICATIVE adjust factors (same convention as Dolomite premiums).
 */
export function exactlyPairLtv(
  collateralAdjustFactor: bigint | undefined,
  borrowAdjustFactor: bigint | undefined,
): number {
  if (!collateralAdjustFactor || !borrowAdjustFactor) return 0
  return (
    (Number(collateralAdjustFactor) / WAD) * (Number(borrowAdjustFactor) / WAD)
  )
}
